import React, { useState } from 'react';
import { Mail, Phone, Clock, MapPin, Send } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Card } from './ui/card';
import { toast } from 'sonner';
import { contactAPI } from '../services/api';

export const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await contactAPI.submitMessage(formData);
      toast.success('Message sent successfully!', {
        description: "We'll get back to you within 24 hours."
      });
      setFormData({ name: '', email: '', message: '' });
    } catch (error) {
      toast.error('Failed to send message', {
        description: 'Please try again or call us directly.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <section id="contact" className="py-20 bg-gradient-to-br from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl lg:text-5xl font-extrabold text-gray-900 mb-4">
            Get in Touch
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Have questions? We're here to help 24/7
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Contact Cards */}
          <div className="space-y-4">
            <Card className="p-6 bg-gradient-to-br from-red-50 to-white border-2 border-red-100">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-red-600 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Phone className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 text-lg mb-2">Call Us</h3>
                  <a href="tel:3057724150" className="text-red-600 font-semibold text-xl hover:text-red-700 transition-colors">
                    (305) 772-4150
                  </a>
                  <p className="text-sm text-gray-600 mt-1">24/7 Roadside Assistance - Anytime, Anywhere</p>
                </div>
              </div>
            </Card>

            <Card className="p-6">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-gray-900 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Mail className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 text-lg mb-2">Email Us</h3>
                  <a href="mailto:info@vargastireautorepair.com" className="text-gray-700 hover:text-red-600 transition-colors">
                    info@vargastireautorepair.com
                  </a>
                  <p className="text-sm text-gray-600 mt-1">We respond within 24 hours</p>
                </div>
              </div>
            </Card>

            <Card className="p-6">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-gray-900 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Clock className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-gray-900 text-lg mb-3">Service Hours</h3>
                  <div className="text-center py-3 bg-gradient-to-r from-red-50 to-orange-50 rounded-lg border-2 border-red-200">
                    <p className="text-2xl font-bold text-red-600">24/7 Roadside Assistance</p>
                    <p className="text-sm text-gray-700 mt-1">Anytime, Anywhere in South & North Florida</p>
                  </div>
                </div>
              </div>
            </Card>

            {/* Service Areas */}
            <Card className="p-6">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-gray-900 rounded-lg flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-gray-900 text-lg mb-3">Service Areas</h3>
                  <div className="space-y-3">
                    <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                      <p className="font-semibold text-blue-900 mb-1">🌴 South Florida</p>
                      <p className="text-xs text-blue-800 leading-relaxed">
                        Miami-Dade • Broward • Palm Beach • Monroe • Collier • Lee • Charlotte • Sarasota • Manatee • Martin • St. Lucie • Indian River • Okeechobee • Hendry • Glades
                      </p>
                    </div>
                    <div className="bg-green-50 p-3 rounded-lg border border-green-200">
                      <p className="font-semibold text-green-900 mb-1">🆕 Now Serving North Florida!</p>
                      <p className="text-sm text-green-800 font-semibold">New Location: Jacksonville, FL</p>
                      <p className="text-xs text-green-700 mt-1">Servicing all of North Florida</p>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          </div>

          {/* Contact Form */}
          <Card className="p-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Send us a Message</h3>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label htmlFor="contact-name" className="mb-2 block">
                  Name *
                </Label>
                <Input
                  id="contact-name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  placeholder="Your name"
                />
              </div>

              <div>
                <Label htmlFor="contact-email" className="mb-2 block">
                  Email Address *
                </Label>
                <Input
                  id="contact-email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder="your@email.com"
                />
              </div>

              <div>
                <Label htmlFor="contact-message" className="mb-2 block">
                  Message *
                </Label>
                <Textarea
                  id="contact-message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={5}
                  placeholder="Tell us how we can help..."
                />
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-red-600 hover:bg-red-700 text-white"
                size="lg"
              >
                {isSubmitting ? (
                  'Sending...'
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Send Message
                  </>
                )}
              </Button>
            </form>
          </Card>
        </div>
      </div>
    </section>
  );
};
